// @flow
/* eslint-disable react/no-unescaped-entities */
import React, { Component } from 'react';
import { Table } from 'semantic-grab-ui/src';
import _ from 'lodash';
import type { StoreState } from 'omega-rtc/reducers';
import { connect } from 'react-redux';
import {
  getCityName, getConditionDropdownList,
  getConditionName, getOperatorDescription,
  getTaxiName,
} from 'omega-rtc/components/form/trident-campaigns/constants/utils';

type Props = {
  item: Object,
  tridentCampaign: Object,
  countries: Array,
  listRuleTypes: Array,
  showAccountID: boolean,
};

// HistoryRecordItem renders a single campaign history record
class HistoryRecordItem extends Component {
  props: Props;

  getRuleEvalExplanation = (record, listRuleTypes, tridentCampaign, countries) => {
    if (!_.isEmpty(_.get(record, 'errMessage'))) {
      return "The event couldn't be evaluated due to a technical issue encountered by the system";
    }

    const condition = _.get(record, 'failedCondition', {});
    const { countryID, targetUserType } = tridentCampaign;
    const eventObjects = _.get(listRuleTypes, targetUserType, []);

    const conditionName = getConditionName(eventObjects, condition.Variable); // e.g. "Pickup City"
    const expected = this.convertValue(condition.Expected, condition.Variable, countries, countryID, eventObjects);
    const actual = this.convertValue(condition.Actual, condition.Variable, countries, countryID, eventObjects);
    const operator = getOperatorDescription(condition.Operator);

    if (condition.Variable === 'var.passenger.segment') {
      return (<React.Fragment>
        The campaign is configured for the <strong> {conditionName} </strong> to be {operator}
        <span style={{ color: '#00b14f', fontWeight: 'bold' }}> {expected}</span>.
      </React.Fragment>);
    }


    return (<React.Fragment>
      The action was not triggered because the user's <strong> {conditionName} </strong> is <span style={{ color: '#c91d08', fontWeight: 'bold' }}> {actual}</span>. <br />
      But the campaign is configured for the {conditionName} to be {operator}
      <span style={{ color: '#00b14f', fontWeight: 'bold' }}> {expected}</span>.
    </React.Fragment>);
  };


  getRuleEvalRecordView = (record) => {
    const err = _.get(record, 'errMessage', '');
    if (!_.isEmpty(err)) {
      return (
        <div>
          Error: {err}
        </div>
      );
    }

    return (
      <div>
        <p>Failed</p>
        <p>Variable: { record.failedCondition.Variable }</p>
        <p>Operator: { record.failedCondition.Operator }</p>
        <p>Expected: { JSON.stringify(record.failedCondition.Expected) }</p>
        <p>Actual: { JSON.stringify(record.failedCondition.Actual) }</p>
      </div>
    );
  };

  getActionRecordView = (record) => {
    const err = _.get(record, 'error', '');
    const failedAt = _.get(record, 'failedAt', '');
    if (_.isEmpty(err)) {
      return (
        <p>Success</p>
      );
    }

    return (
      <div>
        <p>FAILED: (at {failedAt})</p>
        <p>Error: {err}</p>
      </div>
    );
  };

  convertValue(originalValue, conditionVariable, countries, countryID, eventObjects) {
    // Convert cityID into cityName
    if (conditionVariable === 'var.ride.pickupCityID') {
      if (originalValue.startsWith('[') && originalValue.endsWith(']')) { // "[1 2 3]"
        const removedBracket = originalValue.substring(1, originalValue.length - 1);
        return _.map(removedBracket.split(' ').map(Number), (cityID) => {
          return getCityName(countries, countryID, parseInt(cityID, 10));
        }).join(', ');
      }
      return getCityName(countries, countryID, parseInt(originalValue, 10));
    }

    // Convert serviceID into service name
    if (conditionVariable === 'var.ride.serviceID') {
      if (originalValue.startsWith('[') && originalValue.endsWith(']')) { // "[1 2 3]"
        const removedBracket = originalValue.substring(1, originalValue.length - 1);
        return _.map(removedBracket.split(' ').map(Number), (cityID) => {
          return getTaxiName(countries, countryID, parseInt(cityID, 10));
        }).join(', ');
      }
      return getTaxiName(countries, countryID, parseInt(originalValue, 10));
    }

    // Convert dropdown value into the label
    const dropdownValueMap = getConditionDropdownList(eventObjects, conditionVariable);
    if (dropdownValueMap) {
      return _.get(dropdownValueMap, originalValue, originalValue);
    }

    // If no conversion is needed, return the original value
    return originalValue;
  }

  render() {
    const ruleEvalRecord = _.get(this.props.item, 'ruleEvalRecord');
    const actionRecord = _.get(this.props.item, 'actionRecord');
    const baseInfoSource = _.isEmpty(ruleEvalRecord) ? actionRecord : ruleEvalRecord;


    return (
      <div>
        <Table striped celled style={{ marginBottom: 20 }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell colSpan="16">{baseInfoSource.trackingKey}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell collapsing>
                Time
              </Table.Cell>
              <Table.Cell>{baseInfoSource.timestamp}</Table.Cell>
            </Table.Row>
            {
              this.props.showAccountID &&
              <Table.Row>
                <Table.Cell collapsing>
                  Account ID
                </Table.Cell>
                <Table.Cell>{baseInfoSource.accountID}</Table.Cell>
              </Table.Row>
            }
            {
              ruleEvalRecord &&
              <React.Fragment>
                <Table.Row>
                  <Table.Cell collapsing>
                    Rule Evaluation
                  </Table.Cell>
                  <Table.Cell>{this.getRuleEvalRecordView(ruleEvalRecord)}</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell collapsing>
                    Explanation
                  </Table.Cell>
                  <Table.Cell>
                    {this.getRuleEvalExplanation(ruleEvalRecord, this.props.listRuleTypes, this.props.tridentCampaign, this.props.countries)}
                  </Table.Cell>
                </Table.Row>
              </React.Fragment>
            }
            {
              actionRecord &&
              <Table.Row>
                <Table.Cell collapsing>
                  Action Result
                </Table.Cell>
                <Table.Cell>{this.getActionRecordView(actionRecord)}</Table.Cell>
              </Table.Row>
            }
          </Table.Body>
        </Table>
      </div>
    );
  }
}

export function mapStateToProps(state: StoreState) {
  return {
    countries: state.countries.entities,
    tridentCampaign: state.tridentCampaigns.active,
    listRuleTypes: state.newTridents.listRuleTypes,
  };
}

export default connect(mapStateToProps)(HistoryRecordItem);
